import type { ReelSection } from './deriveReelSections';
import type { DisabledReelSection, Reel } from './reelTypes';
import type { ReelSectionOrigin } from '@src/types.ts/story';

/**
 * PLAT-11072 — the stored half of turning a Section off and back on (DE13).
 *
 * The timeline half is two commands the epic already has: `RemoveReelSection`
 * takes the passage out, `InsertReelSections` rebuilds it from the source media.
 * Everything here is what the timeline cannot carry — where the passage was,
 * what it was called, where it came from, and the few strings its row shows
 * while it is away (DS11).
 *
 * All pure and non-mutating; the caller writes the returned reel into
 * `clipJson.reel`.
 */

export type DisableReelSectionInput = {
  /** The Section as the timeline still has it — read before the removal. */
  section: ReelSection;
  /** The row above it in the list; `''` when it opens the reel (DS3). */
  afterSectionId: string;
  /** PLAT-11067 lineage, read off the stored `subClips` before they re-derive. */
  origin?: ReelSectionOrigin[];
};

/**
 * A reel carries `disabledSections` only while something is off, so a reel that
 * has had everything turned back on stores nothing rather than an empty array.
 */
function withDisabled(
  reel: Reel | undefined,
  disabledSections: DisabledReelSection[],
  chapters?: Reel['chapters'],
  sections?: Reel['sections'],
): Reel {
  // `...reel` first, like every other reel writer, so a field a later ticket
  // stores rides across instead of being dropped by a writer that predates it
  // (`reelWritersKeepDisabled.test.ts`).
  const next: Reel = {
    ...reel,
    chapters: chapters ?? [...(reel?.chapters ?? [])],
    sections: sections ?? { ...reel?.sections },
  };
  if (disabledSections.length) next.disabledSections = disabledSections;
  else delete next.disabledSections;
  return next;
}

/**
 * Turns a Section off: its name entry leaves `sections` for the stored entry, so
 * the save's `pruneReelSectionNames` — which drops names for Sections the
 * timeline no longer has — cannot take it with it.
 *
 * The media range is read the way `deriveSubClipsFromTimeline` reads one, so the
 * passage the re-insert rebuilds is the passage the save would have written.
 *
 * **No chapter write in either direction** (DS12): the off row stays in the row
 * list, so a chapter anchored to it still resolves and its heading never moves.
 */
export function disableSectionInReel(
  reel: Reel | undefined,
  { section, afterSectionId, origin }: DisableReelSectionInput,
): Reel {
  const sections = { ...reel?.sections };
  const stored = sections[section.id];
  delete sections[section.id];

  const entry: DisabledReelSection = {
    id: section.id,
    range: {
      start: section.mediaStartTime,
      end: section.mediaStartTime + section.duration,
    },
    afterSectionId,
    openingWords: section.openingWords,
    firstLine: section.firstLine,
    speakerLabels: [...section.speakerLabels],
  };
  if (stored) {
    entry.name = stored.name;
    entry.nameSource = stored.nameSource;
  }
  if (origin?.length) entry.origin = origin;

  return withDisabled(
    reel,
    [...(reel?.disabledSections ?? []), entry],
    undefined,
    sections,
  );
}

/**
 * Turns a Section back on. The re-insert mints a **new** provenance id, so
 * everything that pointed at the old one is repointed here: the stored name, a
 * chapter that opened on it, and another off row anchored behind it — the same
 * re-keying `replaceSectionInReel` does for a swap (DS12).
 *
 * A no-op when the id is not off, so a double click cannot strip a live
 * Section's name.
 */
export function enableSectionInReel(
  reel: Reel | undefined,
  sectionId: string,
  newSectionId: string,
): Reel {
  const disabled = reel?.disabledSections ?? [];
  const entry = disabled.find(section => section.id === sectionId);
  if (!entry) return withDisabled(reel, [...disabled]);

  const sections = { ...reel?.sections };
  if (entry.name) {
    sections[newSectionId] = {
      name: entry.name,
      nameSource: entry.nameSource ?? 'clip',
    };
  }

  const chapters = (reel?.chapters ?? []).map(chapter => {
    if (chapter.firstSectionId === sectionId) {
      return {
        ...chapter,
        firstSectionId: newSectionId,
        afterSectionId: undefined,
      };
    }
    if (chapter.afterSectionId === sectionId) {
      return { ...chapter, afterSectionId: newSectionId };
    }
    return chapter;
  });

  return withDisabled(
    reel,
    disabled
      .filter(section => section.id !== sectionId)
      .map(section =>
        section.afterSectionId === sectionId
          ? { ...section, afterSectionId: newSectionId }
          : section,
      ),
    chapters,
    sections,
  );
}

/**
 * Remove from Reel on a row that is already off (DS7). There is no timeline to
 * change, so the entry is the whole of it — and anything anchored behind it
 * falls back to the row it sat after, which keeps a run of off rows together.
 */
export function removeDisabledSectionFromReel(
  reel: Reel | undefined,
  sectionId: string,
): Reel {
  const disabled = reel?.disabledSections ?? [];
  const entry = disabled.find(section => section.id === sectionId);
  if (!entry) return withDisabled(reel, [...disabled]);

  return withDisabled(
    reel,
    disabled
      .filter(section => section.id !== sectionId)
      .map(section =>
        section.afterSectionId === sectionId
          ? { ...section, afterSectionId: entry.afterSectionId ?? '' }
          : section,
      ),
  );
}

/**
 * PLAT-11072 DS10 — every off row's anchor re-read off the order the creator can
 * see, which is the write a drag makes.
 *
 * Needed in both directions: an off row dragged somewhere new has to record it,
 * and an off row left behind by the live row above it moving away has to record
 * that too — otherwise it would jump on the next re-derivation to wherever its
 * stale anchor now sits.
 *
 * Returns the **same** reel when no anchor moved, so a drag that changed nothing
 * here writes no new object.
 */
export function reanchorDisabledSections(
  reel: Reel | undefined,
  orderedRowIds: readonly string[],
): Reel | undefined {
  const disabled = reel?.disabledSections;
  if (!reel || !disabled?.length) return reel;

  const positionOf = new Map(orderedRowIds.map((id, index) => [id, index]));
  let changed = false;
  const next = disabled.map(entry => {
    const at = positionOf.get(entry.id);
    // Not in the order it was given — an entry the caller is not describing, so
    // its anchor is left as it stands rather than guessed at.
    if (at === undefined) return entry;
    const anchor = at === 0 ? '' : orderedRowIds[at - 1];
    if ((entry.afterSectionId ?? '') === anchor) return entry;
    changed = true;
    return { ...entry, afterSectionId: anchor };
  });

  return changed ? { ...reel, disabledSections: next } : reel;
}
